import { reactive } from 'vue'
import { apiFetch } from './api'

export interface SystemColors {
  primary: string
  secondary: string
  report?: string
}

export interface SystemConfig {
  systemId: string
  systemUsername: string
  systemUsernameChangedAt: string
  siteName: string
  siteLogoUrl: string
  loginBackgroundUrl: string
  usePrimaryLoginBackground: boolean
  colors: SystemColors
  autoSecondary: boolean
  attendanceStartTime: string
  workedWeekdays: string[]
  logoType?: 'default' | 'by-gus' | 'custom'
  customLogoText?: string
  offlineTimeoutDays?: number
  maintenanceEnabled?: boolean
  manualsAiEnabled?: boolean
  maintenanceProntoText?: string
  maintenanceProntoColor?: string
  maintenancePendenteText?: string
  maintenancePendenteColor?: string
  ganttReportMode?: 'ia' | 'offline'
  timezone?: string
  suspended?: boolean
}

const defaults: SystemConfig = {
  systemId: 'relatorioshow',
  systemUsername: 'relatorioshow',
  systemUsernameChangedAt: '',
  siteName: 'RelatorioShow',
  siteLogoUrl: '',
  loginBackgroundUrl: '',
  usePrimaryLoginBackground: true,
  colors: {
    primary: '#0f4c81',
    secondary: '#ffffff'
  },
  autoSecondary: true,
  attendanceStartTime: '07:00',
  workedWeekdays: ['mon', 'tue', 'wed', 'thu', 'fri'],
  logoType: 'default',
  customLogoText: '',
  offlineTimeoutDays: 30
}

const state = reactive({
  config: { ...defaults } as SystemConfig,
  featureFlags: {} as Record<string, boolean>
})

export const systemStore = {
  state,
  get() {
    return state.config
  },
  getLogoText() {
    const config = state.config
    const logoType = config.logoType || 'default'
    if (logoType === 'default') return 'ObraTycoon by GusMachado'
    if (logoType === 'by-gus') return 'by GusMachado'
    if (logoType === 'custom') return config.customLogoText || 'ObraTycoon by GusMachado'
    return 'ObraTycoon by GusMachado'
  },
  async refresh() {
    const payload = await apiFetch<SystemConfig>('/system-config')
    state.config = {
      ...defaults,
      ...payload,
      colors: {
        ...defaults.colors,
        ...(payload?.colors || {})
      }
    }
    return state.config
  },
  async save(config: Partial<SystemConfig>) {
    await apiFetch<{ ok: boolean }>('/system-config', {
      method: 'POST',
      body: JSON.stringify({
        siteName: config.siteName,
        systemUsername: config.systemUsername,
        siteLogoUrl: config.siteLogoUrl,
        loginBackgroundUrl: config.loginBackgroundUrl,
        usePrimaryLoginBackground: config.usePrimaryLoginBackground,
        colors: config.colors || {},
        autoSecondary: config.autoSecondary ?? true,
        attendanceStartTime: config.attendanceStartTime || '07:00',
        workedWeekdays: Array.isArray(config.workedWeekdays) ? config.workedWeekdays : defaults.workedWeekdays,
        logoType: config.logoType || 'default',
        customLogoText: config.customLogoText || '',
        offlineTimeoutDays: config.offlineTimeoutDays ?? 30,
        maintenanceEnabled: config.maintenanceEnabled ?? false,
        manualsAiEnabled: config.manualsAiEnabled ?? true,
        maintenanceProntoText: config.maintenanceProntoText || 'Pronto',
        maintenanceProntoColor: config.maintenanceProntoColor || '#16a34a',
        maintenancePendenteText: config.maintenancePendenteText || 'Pendente procedimento',
        maintenancePendenteColor: config.maintenancePendenteColor || '#dc2626',
        timezone: config.timezone || ''
      })
    })
    await this.refresh()
  },
  async refreshFlags() {
    try {
      const flags = await apiFetch<Array<{ key: string; enabled: boolean | number }>>('/feature-flags')
      const map: Record<string, boolean> = {}
      for (const f of flags) {
        map[f.key] = f.enabled === true || Number(f.enabled) === 1
      }
      state.featureFlags = map
      console.log('[FeatureFlags] loaded:', map)
    } catch (e) {
      console.warn('[FeatureFlags] failed to load:', e)
    }
    return state.featureFlags
  },
  isFeatureEnabled(key: string): boolean {
    if (!(key in state.featureFlags)) return true
    return state.featureFlags[key]
  },
  async refreshPublicByUsername(systemUsername: string) {
    return await apiFetch<SystemConfig>(`/public-system-config/${encodeURIComponent(systemUsername)}`, undefined, false)
  }
}
